import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import fdaApi from '../../../../api/data-sections/fdaApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { FdaEnforcementModel } from '../../../../models/data-sections/fda/enforcementModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface FdaEnforcementModalProps {
    searchExecutionId: string;
    selected_enforcement: FdaEnforcementModel;
}

const FdaEnforcementModal: React.FC<FdaEnforcementModalProps> = ({
    searchExecutionId,
    selected_enforcement
}) => {
    const [enforcementData, setEnforcementData] = useState<FdaEnforcementModel | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didErrorOccur, setDidErrorOccur] = useState(false);
    const { triggerSystemError } = useSystemError();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await fetchApiGetEnforcementDetails(searchExecutionId, selected_enforcement.recall_number, new Date(selected_enforcement.__ci_system_search_date_key ?? 0));
            } catch (error) {
                console.error('Error during fetch:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [searchExecutionId, selected_enforcement]);

    const fetchApiGetEnforcementDetails = async (search_execution_id: string, k_number: string, __ci_system_search_date_key: Date ): Promise<void> => {
        try {
            const response = await fdaApi.getEnforcementDetail(search_execution_id, k_number, __ci_system_search_date_key);

            if (response.success) {
                const data = (response as ResponseSuccessModel).data as any[];
                setEnforcementData(data?.[0] || null);
            } else {
                handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
            }
        } catch (error) {
            handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                <Typography variant="h6">
                    {selected_enforcement.recalling_firm}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                    {selected_enforcement.recall_number}
                </Typography>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            ) : didErrorOccur || !enforcementData ? (
                <Typography color="error">Failed to load Enforcement details.</Typography>
            ) : (
                <Box mt={2}>
                    <Typography><strong>Recalling Firm:</strong> {enforcementData.recalling_firm || 'N/A'}</Typography>
                    <Typography><strong>Status:</strong> {enforcementData.status || 'N/A'}</Typography>
                    <Typography><strong>Reason for Recall:</strong> {enforcementData.reason_for_recall || 'N/A'}</Typography>
                    <Typography><strong>Initiation Date:</strong> {enforcementData.recall_initiation_date || 'N/A'}</Typography>

                    {enforcementData.reason_for_recall && (
                        <Box mt={3}>
                            <Typography variant="h6">Reason for Recall</Typography>
                            <Typography paragraph>{enforcementData.reason_for_recall}</Typography>
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

export default FdaEnforcementModal;
